import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldAlert, ArrowRight, ArrowLeft } from "lucide-react";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";
import FloatingWhatsApp from "../components/FloatingWhatsApp";
import { CmsImageSlideshow as CmsImage } from "../lib/images";
import { api } from "../lib/api";
import { useI18n } from "../i18n";
import { waProps, waLabel } from "../lib/whatsapp";

/**
 * Dedicated editorial page for the XEGA VIP service.
 *
 * Text is the client's verbatim brochure copy adapted to a modern,
 * cinematic layout. Three client-supplied photos are laid out in
 * distinct editorial blocks:
 *   1) City network — hero background (coverage + technology).
 *   2) Chip device  — "a button away" section (discreet wearable).
 *   3) Rope hands   — kidnap prevention block (what it protects from).
 */
export default function XegaVip() {
  const { pick } = useI18n();
  const [content, setContent] = useState(null);

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data?.data || null)).catch(() => {});
    api.post("/track/visit", { path: "/servicios/xega-vip" }).catch(() => {});
  }, []);

  // Original brochure text — kept verbatim in ES and faithfully translated
  const P1_ES = "VIP ofrece una solución que favorece la tranquilidad y seguridad de las familias en caso de que una emergencia inesperada se presente.";
  const P1_EN = "VIP offers a solution that fosters peace of mind and safety for families in the event of an unexpected emergency.";
  const P1_PT = "O VIP oferece uma solução que favorece a tranquilidade e a segurança das famílias no caso de uma emergência inesperada acontecer.";

  const P2_ES = "VIP es el sistema en prevención de secuestros y emergencias más avanzado a nivel mundial, diseñado para operar en los lugares más peligrosos de América Latina.";
  const P2_EN = "VIP is the most advanced kidnap-prevention and emergency system worldwide, designed to operate in the most dangerous places of Latin America.";
  const P2_PT = "O VIP é o sistema de prevenção de sequestros e emergências mais avançado do mundo, projetado para operar nos lugares mais perigosos da América Latina.";

  const P3_ES = "VIP contribuye a la libertad de movimiento de las personas — a nivel gubernamental, empresarial, industrial y privado.";
  const P3_EN = "VIP contributes to freedom of movement — at governmental, corporate, industrial and private levels.";
  const P3_PT = "O VIP contribui para a liberdade de movimento das pessoas — em nível governamental, empresarial, industrial e privado.";

  const P4_ES = "VIP, a un botón de distancia, obtiene en cuestión de segundos la coordinación del equipo más capacitado para apoyar cualquier situación en caso de llegar al peor de los escenarios.";
  const P4_EN = "VIP — one button away — dispatches in seconds the most capable coordination team to support any situation should the worst-case scenario occur.";
  const P4_PT = "O VIP — a um botão de distância — mobiliza em segundos a coordenação da equipe mais capacitada para apoiar qualquer situação diante do pior dos cenários.";

  return (
    <div className="min-h-screen text-white" data-testid="xega-vip-page">
      <NavBar />

      {/* ═══════ HERO ═══════ */}
      <section className="relative overflow-hidden h-[38vh] min-h-[280px] max-h-[420px] bg-[#06080D]">
        <CmsImage
          slotKey="xega_vip.hero"
          defaultSrc="/xega-vip/city-network.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover object-center opacity-75"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#06080D] via-[#06080D]/70 to-[#06080D]/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#06080D] via-transparent to-transparent" />
        <div className="absolute inset-0 grid-bg opacity-[0.08]" />

        <div className="relative h-full max-w-7xl mx-auto px-5 sm:px-6 md:px-10 flex items-end pb-6 md:pb-8 pt-24 sm:pt-28 md:pt-32">
          <div className="max-w-2xl">
            <Link to="/servicios" className="inline-flex items-center gap-2 text-white/60 hover:text-cyan-300 transition" data-testid="back-to-services">
              <ArrowLeft className="w-4 h-4" />
              <span className="label-mono">{pick("SERVICIOS / ALL", "SERVICES / ALL", "SERVIÇOS / ALL")}</span>
            </Link>
          </div>
        </div>

        {/* Centered page title over the banner */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="absolute inset-0 flex items-center justify-center pointer-events-none px-4"
          data-testid="xega-vip-banner-title"
        >
          <span
            className="font-display chrome-text text-4xl sm:text-6xl md:text-7xl lg:text-[6.5rem] tracking-[0.08em] leading-none text-center"
            style={{ filter: 'drop-shadow(0 4px 24px rgba(0,0,0,0.85))' }}
          >
            XEGA VIP
          </span>
        </motion.h1>
        <div className="absolute bottom-0 inset-x-0 h-10 bg-gradient-to-b from-transparent to-[#06080D] pointer-events-none" />
      </section>

      {/* ═══════ BLOCK #1 — Tranquilidad + chip ═══════ */}
      <section className="relative py-4 md:py-8 bg-[#06080D]">
        <div className="absolute top-1/3 -left-40 w-[560px] h-[560px] rounded-full bg-cyan-500/8 blur-[160px] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-14 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
              transition={{ duration: .7 }}
              className="lg:col-span-5 relative"
            >
              <div className="relative rounded-2xl overflow-hidden chrome-border shadow-[0_25px_60px_-15px_rgba(0,240,255,0.15)]">
                <CmsImage
                  slotKey="xega_vip.chip"
                  defaultSrc="/xega-vip/chip-device.jpg"
                  alt={pick("Dispositivo discreto XEGA VIP", "Discreet XEGA VIP device", "Dispositivo discreto XEGA VIP")}
                  className="w-full h-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#06080D]/40 via-transparent to-cyan-500/10 mix-blend-multiply" />
                <div className="absolute bottom-4 left-4 chrome-border rounded-xl px-3 py-2 flex items-center gap-2" style={{ background: 'rgba(6,8,13,0.85)', backdropFilter: 'blur(8px)' }}>
                  <span className="pulse-dot" />
                  <span className="label-mono !text-cyan-300 text-[10px]">{pick("DISPOSITIVO DISCRETO", "DISCREET DEVICE", "DISPOSITIVO DISCRETO")}</span>
                </div>
              </div>
              <div className="absolute -top-2 -left-2 w-6 h-6 border-t border-l border-cyan-400/40 pointer-events-none" />
              <div className="absolute -bottom-2 -right-2 w-6 h-6 border-b border-r border-cyan-400/40 pointer-events-none" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: .7, delay: .1 }}
              className="lg:col-span-7"
            >
              <p className="text-white/85 text-base sm:text-lg leading-relaxed">
                {pick(P1_ES, P1_EN, P1_PT)}
              </p>
              <p className="mt-4 text-white/85 text-base sm:text-lg leading-relaxed">
                {pick(P4_ES, P4_EN, P4_PT)}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════ BLOCK #2 — Prevención de secuestros ═══════ */}
      <section className="relative py-4 md:py-8 bg-[#06080D]">
        <div className="absolute bottom-1/3 -right-40 w-[560px] h-[560px] rounded-full bg-red-500/6 blur-[160px] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-14 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: .7 }}
              className="lg:col-span-7 order-2 lg:order-1"
            >
              <p className="text-white/85 text-base sm:text-lg leading-relaxed">
                {pick(P2_ES, P2_EN, P2_PT)}
              </p>
              <p className="mt-4 text-white/85 text-base sm:text-lg leading-relaxed">
                {pick(P3_ES, P3_EN, P3_PT)}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
              transition={{ duration: .7, delay: .1 }}
              className="lg:col-span-5 order-1 lg:order-2 relative"
            >
              <div className="relative rounded-2xl overflow-hidden chrome-border shadow-[0_25px_60px_-15px_rgba(255,80,80,0.12)]">
                <CmsImage
                  slotKey="xega_vip.rope"
                  defaultSrc="/xega-vip/rope-hands.jpg"
                  alt={pick("Prevención de secuestros y emergencias", "Kidnap and emergency prevention", "Prevenção de sequestros e emergências")}
                  className="w-full h-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-tl from-[#06080D]/70 via-transparent to-red-900/20 mix-blend-multiply" />
                <div className="absolute bottom-4 right-4 chrome-border rounded-xl px-3 py-2 flex items-center gap-2" style={{ background: 'rgba(6,8,13,0.85)', backdropFilter: 'blur(8px)' }}>
                  <ShieldAlert className="w-3.5 h-3.5" style={{ color: '#ff9090' }} />
                  <span className="label-mono text-[10px]" style={{ color: '#ff9090' }}>{pick("PROTOCOLO CRÍTICO", "CRITICAL PROTOCOL", "PROTOCOLO CRÍTICO")}</span>
                </div>
              </div>
              <div className="absolute -top-2 -right-2 w-6 h-6 border-t border-r border-red-400/40 pointer-events-none" />
              <div className="absolute -bottom-2 -left-2 w-6 h-6 border-b border-l border-red-400/40 pointer-events-none" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════ CTA STRIP ═══════ */}
      <section className="relative py-4 md:py-8 bg-[#06080D]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
          <div className="relative overflow-hidden rounded-3xl chrome-border p-8 sm:p-10 md:p-14">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-transparent to-emerald-500/10 pointer-events-none" />

            {/* Ambient soft glow spot */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-[520px] h-[520px] rounded-full"
              style={{ background: 'radial-gradient(circle, rgba(0,240,255,0.10) 0%, transparent 65%)', filter: 'blur(20px)' }}
            />

            <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-8 md:gap-10">
              <motion.h3
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="relative font-display leading-[1.08] tracking-tight max-w-2xl text-[1.6rem] sm:text-3xl md:text-4xl lg:text-[2.6rem] self-center"
                data-testid="vip-cta-headline"
              >
                <span
                  className="chrome-text italic font-semibold"
                  style={{
                    letterSpacing: '-0.005em',
                    filter: 'drop-shadow(0 2px 12px rgba(0,240,255,0.15)) drop-shadow(0 1px 0 rgba(0,0,0,0.6))',
                  }}
                >
                  {pick(
                    "Hablemos del XEGA VIP que tu familia necesita.",
                    "Let's talk about the XEGA VIP your family needs.",
                    "Vamos falar do XEGA VIP que sua família precisa."
                  )}
                </span>
              </motion.h3>

              <a
                {...waProps(content?.contact?.whatsapp ? `https://wa.me/${String(content.contact.whatsapp).replace(/[^0-9]/g,'')}?text=${encodeURIComponent(pick('Hola, me interesa el servicio XEGA VIP','Hi, I am interested in the XEGA VIP service','Olá, tenho interesse no serviço XEGA VIP'))}` : "#contact")}
                className="shrink-0 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-cyan-400 to-emerald-400 text-black font-tech uppercase tracking-wider text-sm shadow-[0_0_28px_-4px_rgba(0,240,255,0.7)] hover:shadow-[0_0_36px_-4px_rgba(0,240,255,0.95)] transition self-start md:self-auto"
                data-testid="vip-cta-whatsapp"
              >
                {waLabel(pick, "Escribir por WhatsApp", "Message on WhatsApp", "Falar pelo WhatsApp")}
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer content={content} />
      <FloatingWhatsApp phone={content?.contact?.whatsapp} />
    </div>
  );
}
